import { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";

import type { FoundationSpecsBlock as FoundationSpecsBlockValue } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { TechSpec } from "../TechSpecBlock";

import styles from "./index.module.scss";

type IProps = Immutable<{ value: FoundationSpecsBlockValue }>;
type AdvancedTechSpecBlock = NonNullable<
    NonNullable<IProps["value"]["additional_details"]>[number] & {
        type: "tech_spec";
    }
>["value"];
type AdvancedTechSpecItem = NonNullable<AdvancedTechSpecBlock["specs"]>[number];

const unwrapRichText = (value: string): string => {
    return value.replace(/class=.rich-text./g, "");
};

const tableInnerHTML = (value: string): string => {
    const tableMatch = /<table\b[^>]*>([\s\S]*?)<\/table>/i.exec(value);
    const tableHTML = tableMatch ? tableMatch[1] : value;
    return tableHTML
        .replace(/^<table\b[^>]*>/i, "")
        .replace(/<\/table>\s*$/i, "")
        .replace(
            /(<tbody[^>]*>)([\s\S]*?)(<\/tbody>)/gi,
            (_match, open: string, body: string, close: string) =>
                `${open}${body.replace(
                    /(<tr[^>]*>\s*)<td([^>]*)>([\s\S]*?)<\/td>/gi,
                    '$1<th scope="row"$2>$3</th>',
                )}${close}`,
        );
};

const renderAdvancedTechSpecItem = (
    item: AdvancedTechSpecItem,
): React.ReactNode => {
    if (item.type === "rich_text") {
        return (
            <div
                className={concatClassNames([
                    styles.blockRichText,
                    "w-block-rich_text",
                ])}
                key={item.id}
                dangerouslySetInnerHTML={{
                    __html: unwrapRichText(item.value),
                }}
            ></div>
        );
    }

    return (
        <div
            className="w-block-table block-table"
            key={item.id}
            dangerouslySetInnerHTML={{ __html: item.value }}
        ></div>
    );
};

const renderAdvancedTechSpec = (
    value: AdvancedTechSpecBlock,
): React.ReactNode => {
    return (
        <TechSpec
            variant="foundation"
            header={value.header}
            spacing={value.spacing}
        >
            {(value.specs ?? []).map(renderAdvancedTechSpecItem)}
        </TechSpec>
    );
};

const renderAdditionalDetail = (
    detail: NonNullable<IProps["value"]["additional_details"]>[number],
): React.ReactNode => {
    if (detail.type === "tech_spec") {
        return (
            <div
                className={concatClassNames([
                    styles.blockTechSpec,
                    "w-block-tech_spec",
                ])}
                key={detail.id}
            >
                {renderAdvancedTechSpec(detail.value)}
            </div>
        );
    }

    if (detail.type === "rich_text") {
        return (
            <div
                className={concatClassNames([
                    styles.blockRichText,
                    "w-block-rich_text",
                ])}
                key={detail.id}
                dangerouslySetInnerHTML={{
                    __html: detail.value,
                }}
            ></div>
        );
    }

    return (
        <EditorContent key={detail.id}>
            {(marker) => (
                <div
                    className="w-block-html block-html"
                    dangerouslySetInnerHTML={{ __html: detail.value }}
                    {...marker}
                ></div>
            )}
        </EditorContent>
    );
};

export const FoundationSpecsBlock = (props: IProps): React.JSX.Element => {
    return (
        <section
            className={concatClassNames([
                styles.foundationSpecBlock,
                "pdp-features",
                props.value.background_style?.style,
                getGlobalSpacingClasses(props.value.spacing),
            ])}
        >
            <div className={styles.foundationSpecBlockContainer}>
                <h2 className={styles.foundationSpecBlockMainHeader}>
                    {props.value.header}
                </h2>
                {props.value.specs_table ? (
                    <table
                        className={styles.foundationSpecBlockSpecTable}
                        dangerouslySetInnerHTML={{
                            __html: tableInnerHTML(props.value.specs_table),
                        }}
                    ></table>
                ) : null}
                <div className={styles.foundationSpecBlockAdditionalDetails}>
                    <h3 className={styles.foundationSpecBlockSubheader}>
                        Additional Details
                    </h3>
                    {(props.value.additional_details ?? []).map(
                        renderAdditionalDetail,
                    )}
                </div>
            </div>
        </section>
    );
};
